import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, CameraIcon } from 'lucide-react';
const images = [{
  src: 'https://cdn.magicpatterns.com/uploads/8JRQDsuqZUkqB6JnqLexNf/IA2.png',
  alt: 'Beautiful front yard landscaping with fresh mulch and brick walkway'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/asCAjMBsSNdMJfzmBU1YdJ/Ia7.png',
  alt: 'Stunning backyard patio with stone fire pit and manicured lawn'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/vP33kVAJeRHAoXQJCzqc9j/IA8.png',
  alt: 'Detail of clean mulch edging with stone border and hostas'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/r8jJbfK2M12thh4YNWwhiD/1111.png',
  alt: 'Modern front yard design with ornamental plants and fresh lawn stripes'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/ntwTu57XNU3nsFpcqvYH3Y/IA9.png',
  alt: 'Expansive front yard with curved mulch beds, flowering shrubs, and lush green lawn'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/pJW9118dUxU2N2aWFPMJgS/IA10.png',
  alt: 'Elegant estate landscaping with stone retaining walls, ornamental tree, and fresh mulch'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/5QAHwhxayWaXzY8wUwp74G/IA11.png',
  alt: 'Beautiful curved stamped concrete walkway with decorative border and green lawn'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/wQe2TyRLsh5ZuSRiknzojH/IA12.png',
  alt: 'Pristine front yard with striped lawn, colorful flower beds, and fresh mulch edging'
}];
export function ProjectGallery() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const next = useCallback(() => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % images.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + images.length) % images.length);
  }, []);
  useEffect(() => {
    const timer = setInterval(next, 4500);
    return () => clearInterval(timer);
  }, [next]);
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.95
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.95
    })
  };
  return <section className="py-20 bg-white" data-id="element-250">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-251">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-252">
          <div className="inline-flex items-center gap-2 bg-[#081C15]/10 text-[#081C15] px-4 py-2 rounded-full text-sm font-medium mb-4" data-id="element-253">
            <CameraIcon className="w-4 h-4" data-id="element-254" />
            Our Portfolio
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#081C15] mb-4" data-id="element-255">
            Projects We're Proud Of
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-256">
            Every yard tells a story. Here's a look at some of our recent
            transformations across Northeast Ohio.
          </p>
        </motion.div>

        <div className="relative" data-id="element-257">
          {/* Carousel container */}
          <div className="relative aspect-[16/10] sm:aspect-[16/9] rounded-2xl overflow-hidden shadow-2xl bg-gray-100" data-id="element-258">
            <AnimatePresence initial={false} custom={direction} mode="popLayout" data-id="element-259">
              <motion.img key={current} src={images[current].src} alt={images[current].alt} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.5,
              ease: [0.32, 0.72, 0, 1]
            }} className="absolute inset-0 w-full h-full object-cover" data-id="element-260" />
            </AnimatePresence>

            {/* Gradient overlay at bottom */}
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" data-id="element-261" />
          </div>

          {/* Navigation arrows */}
          <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm p-2.5 rounded-full shadow-lg hover:bg-white transition-colors z-10" aria-label="Previous image" data-id="element-262">
            <ChevronLeftIcon className="w-5 h-5 text-[#081C15]" data-id="element-263" />
          </button>
          <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm p-2.5 rounded-full shadow-lg hover:bg-white transition-colors z-10" aria-label="Next image" data-id="element-264">
            <ChevronRightIcon className="w-5 h-5 text-[#081C15]" data-id="element-265" />
          </button>
        </div>

        {/* Dots indicator */}
        <div className="flex justify-center gap-2.5 mt-6" data-id="element-266">
          {images.map((_, index) => <button key={index} onClick={() => {
          setDirection(index > current ? 1 : -1);
          setCurrent(index);
        }} className={`h-2.5 rounded-full transition-all duration-300 ${index === current ? 'w-8 bg-[#1B4332]' : 'w-2.5 bg-[#1B4332]/20 hover:bg-[#1B4332]/40'}`} aria-label={`Go to image ${index + 1}`} data-id="element-267" />)}
        </div>
      </div>
    </section>;
}